// Game data normalization utilities
// Converts different game formats to a common structure expected by DataTable

/**
 * Normalize WNBA games from frontend API to match MLB structure
 * @param {Array} games - Array of WNBA games from frontend API
 * @returns {Array} Normalized games compatible with DataTable
 */
export const normalizeWNBAGames = (games, sport = 'wnba') => {
  return games.map(game => {
    // Handle different team data structures
    const awayTeam = game.away;
    const homeTeam = game.home;

    // If teams are objects (WNBA/NBA format), extract abbreviations
    // Also check for awayTeamAbbrev/homeTeamAbbrev fields
    const awayAbbrev = game.awayTeamAbbrev || game.awayAbbreviation || (typeof awayTeam === 'object' ? awayTeam.abbreviation : awayTeam);
    const homeAbbrev = game.homeTeamAbbrev || game.homeAbbreviation || (typeof homeTeam === 'object' ? homeTeam.abbreviation : homeTeam);

    // Get scores from team objects if available, fallback to root level
    const awayScore = typeof awayTeam === 'object' ? awayTeam.score : game.awayScore;
    const homeScore = typeof homeTeam === 'object' ? homeTeam.score : game.homeScore;

    // Extract logo URLs from awayLogos and homeLogos arrays
    // Look for the dark mode logo (rel includes "dark" but not "scoreboard")
    let awayLogo = null;
    let homeLogo = null;

    if (game.awayLogos && Array.isArray(game.awayLogos)) {
      const darkLogo = game.awayLogos.find(logo =>
        logo.rel && logo.rel.includes('dark') && !logo.rel.includes('scoreboard')
      );
      awayLogo = darkLogo?.href || game.awayLogos[0]?.href;
    } else if (game.awayLogo) {
      // Use singular awayLogo if arrays don't exist
      awayLogo = game.awayLogo;
    }

    if (game.homeLogos && Array.isArray(game.homeLogos)) {
      const darkLogo = game.homeLogos.find(logo =>
        logo.rel && logo.rel.includes('dark') && !logo.rel.includes('scoreboard')
      );
      homeLogo = darkLogo?.href || game.homeLogos[0]?.href;
    } else if (game.homeLogo) {
      // Use singular homeLogo if arrays don't exist
      homeLogo = game.homeLogo;
    }

    // WNBA: override with 500-dark logo URLs since API-provided logos may be the light variant
    if (sport === 'wnba') {
      if (awayAbbrev) {
        awayLogo = `https://a.espncdn.com/i/teamlogos/wnba/500-dark/${String(awayAbbrev).toLowerCase()}.png`;
      }
      if (homeAbbrev) {
        homeLogo = `https://a.espncdn.com/i/teamlogos/wnba/500-dark/${String(homeAbbrev).toLowerCase()}.png`;
      }
    }

    return {
      ...game,
      // Normalize team fields for DataTable compatibility
      away: awayAbbrev,
      home: homeAbbrev,
      awayTeam: awayAbbrev, // For logo lookups
      homeTeam: homeAbbrev, // For logo lookups

      // Use scores from team objects (more accurate for WNBA/NBA)
      awayScore: awayScore,
      homeScore: homeScore,

      // Add logo URLs
      awayLogo: awayLogo,
      homeLogo: homeLogo,

      // Keep team nicknames if they exist
      awayNickname: game.awayNickname,
      homeNickname: game.homeNickname,

      // Team IDs (NBA uses awayId/homeId)
      awayTeamId: game.awayTeamId || game.awayId,
      homeTeamId: game.homeTeamId || game.homeId,

      // Keep original team objects for reference
      awayTeamData: typeof awayTeam === 'object' ? awayTeam : null,
      homeTeamData: typeof homeTeam === 'object' ? homeTeam : null,

      // Ensure gameIDmlb exists (DataTable expects this field name)
      gameIDmlb: game.gameIDmlb || game.gameId || game.id,

      // Game time - preserve full date/time if available
      gameTime: game.commence_time || game.startTime || game.gameTime || game.date,

      // Preserve sport field
      sport: game.sport,

      // Handle status normalization - status can be string or object
      // Priority: shortDetail (most accurate) > type.name > status string
      status: (() => {
        if (typeof game.status === 'object') {
          // If shortDetail is "Final", use that
          if (game.status?.type?.shortDetail === 'Final') {
            return 'Final';
          }
          // Otherwise use type.name
          return game.status?.type?.name || 'Scheduled';
        }
        return game.status || 'Scheduled';
      })(),

      // Preserve full status object for additional info (clock, period, etc.)
      statusDetail: typeof game.status === 'object' ? game.status : null
    };
  });
};

/**
 * Normalize soccer games from frontend API to match expected structure
 * @param {Array} games - Array of soccer games from frontend API
 * @returns {Array} Normalized games compatible with DataTable
 */
export const normalizeSoccerGames = (games) => {
  return games.map(game => {
    return {
      ...game,
      // Ensure gameIDmlb exists (DataTable expects this field name)
      gameIDmlb: game.gameIDmlb || game.gameId || game.id,

      // Ensure team abbreviations are present
      away: game.away || game.awayTeam,
      home: game.home || game.homeTeam,
      awayTeam: game.awayTeam || game.away,
      homeTeam: game.homeTeam || game.home,

      // Keep full team names if available
      awayTeamFull: game.awayTeamFull || game.awayTeam,
      homeTeamFull: game.homeTeamFull || game.homeTeam,

      // Team IDs
      awayTeamId: game.awayTeamId,
      homeTeamId: game.homeTeamId,

      // Include team logos
      awayLogo: game.awayTeamLogo || game.awayLogo,
      homeLogo: game.homeTeamLogo || game.homeLogo,

      // League info (for filtering like NCAAB conferences)
      leagueId: game.leagueId,
      leagueName: game.leagueName,

      // Game time - prefer startTimeEST for full datetime with timezone
      gameTime: game.startTimeEST || game.commence_time || game.startTime || game.gameTime || game.date,

      // Handle status normalization
      status: game.status || 'Scheduled',

      // Ensure sport field
      sport: 'soccer'
    };
  });
};

/**
 * Normalize NCAAF games from frontend API to match expected structure
 * @param {Array} games - Array of NCAAF games from frontend API
 * @returns {Array} Normalized games compatible with DataTable
 */
export const normalizeNCAAfGames = (games) => {
  return games.map(game => {
    return {
      ...game,
      // Ensure gameIDmlb exists (DataTable expects this field name)
      gameIDmlb: game.gameIDmlb || game.gameId || game.id,
      
      // Ensure scores are properly mapped
      awayScore: game.awayScore,
      homeScore: game.homeScore,
      
      // Handle status normalization
      status: game.status || 'Scheduled',
      
      // Ensure sport field
      sport: 'ncaaf'
    };
  });
};

/**
 * Normalize NFL games from frontend API to match expected structure
 * @param {Array} games - Array of NFL games from frontend API
 * @returns {Array} Normalized games compatible with DataTable
 */
export const normalizeNFLGames = (games) => {
  return games.map(game => {
    return {
      ...game,
      // Ensure gameIDmlb exists (DataTable expects this field name)
      gameIDmlb: game.gameIDmlb || game.gameId || game.id,
      
      // Ensure scores are properly mapped
      awayScore: game.awayScore,
      homeScore: game.homeScore,
      
      // Handle status normalization
      status: game.status || 'Scheduled',
      
      // Ensure sport field
      sport: 'nfl'
    };
  });
};

/**
 * Normalize NCAAB games from frontend API to match expected structure
 * @param {Array} games - Array of NCAAB games from frontend API
 * @returns {Array} Normalized games compatible with DataTable
 */
// Compact a conference display name for the filter toggles.
// Strips the trailing " Conference" suffix (and the "The " prefix on names
// like "The American Athletic Conference") since the backend doesn't emit
// a shortName field for NCAAB — e.g. "Big East Conference" -> "Big East",
// "American Athletic Conference" -> "American Athletic". Known multi-word
// aliases that have a well-known short form are mapped explicitly so they
// read like NCAAF ("SEC", "ACC", "Big 12", ...).
const NCAAB_CONF_ALIASES = {
  'Atlantic Coast Conference': 'ACC',
  'Southeastern Conference': 'SEC',
  'Big Ten Conference': 'Big Ten',
  'Big 12 Conference': 'Big 12',
  'Pac-12 Conference': 'Pac-12',
  'American Athletic Conference': 'American',
  'Mountain West Conference': 'Mountain West',
  'Conference USA': 'C-USA',
  'Mid-American Conference': 'MAC',
  'Sun Belt Conference': 'Sun Belt',
  'Atlantic 10 Conference': 'A-10',
  'Missouri Valley Conference': 'MVC',
  'West Coast Conference': 'WCC',
  'Mountain West': 'Mountain West',
};
const shortenConferenceName = (name) => {
  if (!name) return null;
  if (NCAAB_CONF_ALIASES[name]) return NCAAB_CONF_ALIASES[name];
  return String(name)
    .replace(/^The\s+/i, '')
    .replace(/\s+Conference$/i, '')
    .trim();
};

export const normalizeNCAABGames = (games) => {
  return games.map(game => {
    // Extract scores from various possible locations
    const awayTeamObj = game.awayTeamObj || game.competitors?.find(c => c.homeAway === 'away');
    const homeTeamObj = game.homeTeamObj || game.competitors?.find(c => c.homeAway === 'home');

    // Try multiple sources for scores (including awayTeamDetails/homeTeamDetails)
    // Use || instead of ?? to also fall through on empty strings
    const awayScore = game.awayScore || game.awayTeamDetails?.score || awayTeamObj?.score || game.away_score || '';
    const homeScore = game.homeScore || game.homeTeamDetails?.score || homeTeamObj?.score || game.home_score || '';

    // Extract abbreviations (same pattern as NBA)
    const awayAbbrev = game.awayAbbreviation || game.awayTeamAbbrev || game.away;
    const homeAbbrev = game.homeAbbreviation || game.homeTeamAbbrev || game.home;

    return {
      ...game,
      // Ensure gameIDmlb exists (DataTable expects this field name)
      gameIDmlb: game.gameId || game.gameIDmlb || game.id,

      // Normalize team fields - use abbreviation for display
      away: awayAbbrev,
      home: homeAbbrev,
      awayTeam: awayAbbrev,
      homeTeam: homeAbbrev,

      // Keep full team names (NCAAB API provides awayTeamFull/homeTeamFull)
      awayTeamFull: game.awayTeamFull || game.away,
      homeTeamFull: game.homeTeamFull || game.home,

      // Team IDs
      awayTeamId: game.awayTeamId,
      homeTeamId: game.homeTeamId,

      // Include team logos (NCAAB provides direct logo URLs)
      awayLogo: game.awayLogo,
      homeLogo: game.homeLogo,

      // Scores - normalized from multiple possible sources
      awayScore: awayScore,
      homeScore: homeScore,

      // Conference info — prefer the shortName variant when the backend
      // provides it, otherwise compact the full name client-side so toggles
      // read like NCAAF ("SEC" not "Southeastern Conference", "Big East"
      // not "Big East Conference"). See shortenConferenceName above.
      awayConferenceName: shortenConferenceName(
        game.awayConferenceShortName || game.awayConferenceName || game.awayConference || game.awayConf
      ),
      homeConferenceName: shortenConferenceName(
        game.homeConferenceShortName || game.homeConferenceName || game.homeConference || game.homeConf
      ),

      // Records (array of record objects)
      awayRecord: game.awayRecord,
      homeRecord: game.homeRecord,

      // Rankings (99 = unranked)
      awayRank: game.awayRank,
      homeRank: game.homeRank,

      // Venue info
      venue: game.venue,
      venueCity: game.venueCity,
      venueState: game.venueState,

      // Network info
      network: game.network,

      // Game time
      gameTime: game.gameTime,

      // Handle status normalization
      status: game.status || 'Scheduled',

      // Status detail for live games
      statusDetail: game.statusDetail,

      // Period and clock for live games
      period: game.period,
      clock: game.clock,

      // Ensure sport field
      sport: 'ncaab'
    };
  });
};

/**
 * Normalize NHL games from frontend API to match expected structure
 * @param {Array} games - Array of NHL games from frontend API
 * @returns {Array} Normalized games compatible with DataTable
 */
export const normalizeNHLGames = (games) => {
  return games.map(game => {
    return {
      ...game,
      // Ensure gameIDmlb exists (DataTable expects this field name)
      gameIDmlb: game.gameId || game.gameIDmlb || game.id,

      // Normalize team fields
      away: game.awayTeamAbbrev || game.away,
      home: game.homeTeamAbbrev || game.home,
      awayTeam: game.awayTeamAbbrev || game.awayTeam || game.away,
      homeTeam: game.homeTeamAbbrev || game.homeTeam || game.home,

      // Keep full team names
      awayTeamFull: game.awayTeam || game.away,
      homeTeamFull: game.homeTeam || game.home,

      // Team IDs
      awayTeamId: game.awayTeamId,
      homeTeamId: game.homeTeamId,

      // Include team logos (NHL provides SVG logos)
      awayLogo: game.awayTeamLogo || game.awayLogo,
      homeLogo: game.homeTeamLogo || game.homeLogo,

      // Scores
      awayScore: game.awayScore,
      homeScore: game.homeScore,

      // Game time - preserve full date/time if available
      gameTime: game.commence_time || game.startTime || game.gameTime,

      // Handle status normalization
      status: game.status || game.gameState || 'Scheduled',

      // Period info for live games
      period: game.period,
      clock: game.clock,

      // Ensure sport field
      sport: 'nhl'
    };
  });
};

/**
 * Normalize games based on sport type
 * @param {Array} games - Array of games
 * @param {string} sport - Sport type ('mlb', 'nba', 'wnba', 'ncaaf', 'nfl', 'nhl', etc.)
 * @returns {Array} Normalized games
 */
export const normalizeGamesBySport = (games, sport) => {
  if (!Array.isArray(games)) {
    return [];
  }

  switch (sport.toLowerCase()) {
    case 'nba':
    case 'wnba':
      return normalizeWNBAGames(games, sport.toLowerCase());
    case 'soccer':
      return normalizeSoccerGames(games);
    case 'ncaaf':
      return normalizeNCAAfGames(games);
    case 'ncaab':
      return normalizeNCAABGames(games);
    case 'nfl':
      return normalizeNFLGames(games);
    case 'nhl':
      return normalizeNHLGames(games);
    case 'mlb':
    default:
      // MLB games are already in the correct format
      return games;
  }
};

/**
 * Get team logo URL based on sport and team data
 * @param {string|Object} team - Team abbreviation or team object
 * @param {string} sport - Sport type
 * @param {Function} getTeamLogoUrlForSport - Logo URL function
 * @returns {string} Logo URL
 */
export const getTeamLogo = (team, sport, getTeamLogoUrlForSport) => {
  // If team is an object with logo, use that
  if (typeof team === 'object' && team.logo) {
    return team.logo;
  }
  
  // Otherwise use the sport-specific logo function
  const abbreviation = typeof team === 'object' ? team.abbreviation : team;
  return getTeamLogoUrlForSport(abbreviation);
};